import React from 'react'
import {
  AlignItems,
  Body2,
  Bold,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Icon,
  IconVariant,
  Image,
  JustifyContent,
  Link,
  Space,
  Text,
} from '@gousto-internal/citrus-react'

import type {
  LoggedOutBoxSummaryActions,
  LoggedOutBoxSummarySummaryInfoBanner,
  LoggedOutBoxSummarySummaryPriceBreakdown,
  LoggedOutBoxSummarySummaryRecipeItem,
  LoggedOutBoxSummarySummaryTooManyRecipesWarning,
  LoggedOutBoxSummarySummaryView,
} from '../types'
import { PanelContinueButton } from './PanelContinueButton'

type SummaryPanelProps = {
  view: LoggedOutBoxSummarySummaryView
  actions: Pick<
    LoggedOutBoxSummaryActions,
    | 'onSummaryEditDelivery'
    | 'onSummaryEditPortionSize'
    | 'onSummaryRecipeRemove'
    | 'onSummaryRecipeClick'
    | 'onSummaryRestoreDeliveryDate'
    | 'onCheckout'
  >
}

const EditRow = ({
  label,
  onClick,
  testId,
}: {
  label: string
  onClick: () => void
  testId: string
}) => (
  <Button
    colorVariant={ButtonColorVariant.Secondary}
    width="100%"
    height="48px"
    onClick={onClick}
    data-testid={testId}
  >
    <Box
      display={Display.Flex}
      width="100%"
      justifyContent={JustifyContent.SpaceBetween}
      alignItems={AlignItems.Center}
    >
      <Text size={[3, null, 5]} fontFamily={FontFamily.Bold}>
        {label}
      </Text>
      <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
        <Icon size={4} name="edit" variant={IconVariant.Actionable} />
        <Text size={1} fontFamily={FontFamily.Bold}>
          edit
        </Text>
      </Box>
    </Box>
  </Button>
)

const RecipeItem = ({
  recipe,
  onRemove,
  onRecipeClick,
}: {
  recipe: LoggedOutBoxSummarySummaryRecipeItem
  onRemove: () => void
  onRecipeClick: () => void
}) => (
  <Box
    display={Display.Flex}
    alignItems={AlignItems.Center}
    gap={3}
    paddingV={3}
    data-testid={`logged-out-box-summary-recipe-item-${recipe.id}`}
    // Dim and disable interaction for unavailable recipes, matching the
    // control's `.itemUnavailable` CSS class (opacity: 0.5, pointer-events: none).
    style={recipe.isUnavailable ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
  >
    <Box
      width="48px"
      height="48px"
      onClick={onRecipeClick}
      borderRadius={'round'}
      style={{ overflow: 'hidden', flexShrink: 0, cursor: 'pointer' }}
    >
      <Image src={recipe.imageUrl} width="48px" height="48px" fit="cover" alt="" />
    </Box>
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} style={{ flexGrow: 1 }}>
      <Link size={2} onClick={onRecipeClick} style={{ boxShadow: 'none' }}>
        <Body2 color={Color.ColdGrey_900}>
          <Bold>{recipe.name} &gt;</Bold>
        </Body2>
      </Link>
      <Body2 color={Color.ColdGrey_800}>{recipe.servings} servings</Body2>
    </Box>
    {!recipe.isUnavailable && (
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${recipe.name}`}
        data-testid={`logged-out-box-summary-recipe-remove-${recipe.id}`}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon name="clear" variant={IconVariant.Default} />
      </button>
    )}
  </Box>
)

const boldNumbers = (message: string) =>
  message.split(/(\d+)/).map((part, i) => (/^\d+$/.test(part) ? <Bold key={i}>{part}</Bold> : part))

const TooManyRecipesWarning = ({
  warning,
}: {
  warning: LoggedOutBoxSummarySummaryTooManyRecipesWarning
}) => (
  <Box
    bg={Color.Error_50}
    display={Display.Flex}
    paddingH={4}
    paddingV={4}
    alignItems={AlignItems.FlexStart}
    borderColor={Color.Error_100}
    borderWidth={0.5}
    borderStyle={BorderStyle.Solid}
    borderRadius={1.5}
    gap={2}
    data-testid="logged-out-box-summary-too-many-recipes-warning"
  >
    <Icon name="error" variant={IconVariant.Error} />
    <Box>
      <Body2 color={Color.ColdGrey_900}>
        <Bold>{warning.title}</Bold>
      </Body2>
      <Body2 color={Color.ColdGrey_800}>{warning.body}</Body2>
    </Box>
  </Box>
)

const SummaryInfoBanner = ({ banner }: { banner: LoggedOutBoxSummarySummaryInfoBanner }) => {
  const isSuccess = banner.variant === 'success'

  return (
    <Box
      bg={isSuccess ? Color.Success_50 : Color.Informative_50}
      display={Display.Flex}
      paddingH={4}
      paddingV={4}
      alignItems={AlignItems.FlexStart}
      borderColor={isSuccess ? Color.Success_100 : Color.Informative_50}
      borderWidth={0.5}
      borderStyle={BorderStyle.Solid}
      borderRadius={1.5}
      gap={2}
      data-testid="logged-out-box-summary-info-banner"
    >
      <Icon
        name={isSuccess ? 'success' : 'info'}
        variant={isSuccess ? IconVariant.Confirmation : IconVariant.Informative}
      />
      <Body2 color={Color.ColdGrey_800}>{boldNumbers(banner.message)}</Body2>
    </Box>
  )
}

const PRICE_PLACEHOLDER = '£—'

const PriceBreakdown = ({
  priceBreakdown,
}: {
  priceBreakdown: LoggedOutBoxSummarySummaryPriceBreakdown
}) => (
  <Box
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={2}
    data-testid="logged-out-box-summary-price-breakdown"
  >
    <Text size={7} fontFamily={FontFamily.Bold} color={Color.ColdGrey_900}>
      Box total
    </Text>
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      data-testid="logged-out-box-summary-price-recipes"
    >
      <Body2 color={Color.ColdGrey_800}>
        {priceBreakdown.recipeCount > 1 ? 'Recipes' : 'Recipe'}
        {priceBreakdown.recipeCount > 0 ? ` (${priceBreakdown.recipeCount})` : ''}
      </Body2>
      <Body2 color={Color.ColdGrey_800}>{priceBreakdown.recipeTotal ?? PRICE_PLACEHOLDER}</Body2>
    </Box>
    {priceBreakdown.discountLabel && priceBreakdown.discountTotal ? (
      <Box
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        data-testid="logged-out-box-summary-price-discount"
      >
        <Body2 color={Color.Success_800}>
          <Bold>{priceBreakdown.discountLabel}</Bold>
        </Body2>
        <Body2 color={Color.Success_800}>
          <Bold>{priceBreakdown.discountTotal}</Bold>
        </Body2>
      </Box>
    ) : null}
    {priceBreakdown.surchargeTotal ? (
      <Box
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        data-testid="logged-out-box-summary-price-surcharges"
      >
        <Body2 color={Color.ColdGrey_800}>Surcharges and/or savings</Body2>
        <Body2 color={Color.ColdGrey_800}>{priceBreakdown.surchargeTotal}</Body2>
      </Box>
    ) : null}
    <Box height="1px" bg={Color.ColdGrey_100} />
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      data-testid="logged-out-box-summary-price-delivery"
    >
      <Body2 color={Color.ColdGrey_800}>Delivery</Body2>
      <Body2 color={Color.ColdGrey_800}>{priceBreakdown.deliveryTotal ?? PRICE_PLACEHOLDER}</Body2>
    </Box>
    <Box height="1px" bg={Color.ColdGrey_100} />
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      data-testid="logged-out-box-summary-price-total"
    >
      <Text size={3} fontFamily={FontFamily.Bold} color={Color.ColdGrey_900}>
        Total
      </Text>
      <Text size={3} fontFamily={FontFamily.Bold} color={Color.ColdGrey_900}>
        {priceBreakdown.total ?? PRICE_PLACEHOLDER}
      </Text>
    </Box>
  </Box>
)

export const SummaryPanel = ({ view, actions }: SummaryPanelProps) => {
  const isCheckoutDisabled = view.checkoutCta.isDisabled || view.checkoutCta.isPending

  const handleCheckout = () => {
    if (!isCheckoutDisabled) {
      actions.onCheckout()
    }
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      paddingH={3}
      paddingBottom={6}
      data-testid="logged-out-box-summary-summary-panel"
    >
      <EditRow
        label={view.deliveryLabel ?? 'Select delivery date'}
        onClick={actions.onSummaryEditDelivery}
        testId="logged-out-box-summary-delivery-edit"
      />

      <EditRow
        label={view.portionSizeLabel}
        onClick={actions.onSummaryEditPortionSize}
        testId="logged-out-box-summary-portion-size-edit"
      />

      <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
        <Text size={7} fontFamily={FontFamily.Bold} color={Color.ColdGrey_900}>
          Recipes
        </Text>
        {view.recipes.length === 0 && view.emptyRecipesCopy ? (
          <>
            <Space size={2} direction="vertical" />
            <Body2 color={Color.ColdGrey_800}>{view.emptyRecipesCopy}</Body2>
          </>
        ) : (
          view.recipes.length > 0 && (
            <>
              <Space size={2} direction="vertical" />
              {view.recipes.map((recipe, index) => (
                <React.Fragment key={recipe.id}>
                  {index > 0 && <Box height="1px" bg={Color.ColdGrey_100} />}
                  <RecipeItem
                    recipe={recipe}
                    onRemove={() => actions.onSummaryRecipeRemove(recipe.id)}
                    onRecipeClick={() => actions.onSummaryRecipeClick(recipe.id)}
                  />
                </React.Fragment>
              ))}
            </>
          )
        )}
      </Box>

      {view.unavailableRecipesWarning && (
        <Box
          bg={Color.Error_50}
          display={Display.Flex}
          paddingH={4}
          paddingV={4}
          alignItems={AlignItems.FlexStart}
          borderColor={Color.Error_100}
          borderWidth={0.5}
          borderStyle={BorderStyle.Solid}
          borderRadius={1.5}
          gap={2}
          data-testid="logged-out-box-summary-unavailable-warning"
        >
          <Icon name="warning" variant={IconVariant.Error} />
          <Body2 color={Color.ColdGrey_800}>
            {view.unavailableRecipesWarning}
            {view.canRestoreDeliveryDate && (
              <>
                {' '}
                Or&nbsp;
                <Link
                  size={2}
                  onClick={actions.onSummaryRestoreDeliveryDate}
                  data-testid="logged-out-box-summary-restore-delivery-date"
                >
                  undo your date change
                </Link>
                .
              </>
            )}
          </Body2>
        </Box>
      )}

      {view.tooManyRecipesWarning && <TooManyRecipesWarning warning={view.tooManyRecipesWarning} />}

      {view.infoBanner && <SummaryInfoBanner banner={view.infoBanner} />}

      {view.priceBreakdown && <PriceBreakdown priceBreakdown={view.priceBreakdown} />}

      <PanelContinueButton
        cta={view.checkoutCta}
        onClick={handleCheckout}
        testId="logged-out-box-summary-checkout"
      />
    </Box>
  )
}
